import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  EmailAuthProvider,
  User,
  UserCredential,
  browserLocalPersistence,
  onAuthStateChanged,
  reauthenticateWithCredential,
  setPersistence,
  signInAnonymously,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { AppSession } from './app.interface';
import { FirebaseService } from './firebase.service';
import { restaurantLoginEmail } from './restaurant-login';

interface ExchangeTableQrResponse {
  restaurantId: string;
  tableId: string;
}

interface PlatformOwnerResponse {
  authorized: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly sessionSubject = new BehaviorSubject<AppSession | null>(null);
  private readonly authReady: Promise<void>;

  constructor(private firebase: FirebaseService) {
    this.authReady = new Promise((resolve) => {
      let initialized = false;
      onAuthStateChanged(
        this.firebase.auth,
        async (user) => {
          try {
            this.sessionSubject.next(await this.createSession(user));
          } catch {
            this.sessionSubject.next(null);
          } finally {
            if (!initialized) {
              initialized = true;
              resolve();
            }
          }
        },
        () => {
          this.sessionSubject.next(null);
          if (!initialized) {
            initialized = true;
            resolve();
          }
        }
      );
    });
  }

  get session$(): Observable<AppSession | null> {
    return this.sessionSubject.asObservable();
  }

  async getSession(): Promise<AppSession | null> {
    await this.authReady;
    return this.sessionSubject.value;
  }

  async loginAdmin(restaurantId: string, password: string): Promise<AppSession> {
    await setPersistence(this.firebase.auth, browserLocalPersistence);
    let credentials: UserCredential;
    try {
      credentials = await signInWithEmailAndPassword(
        this.firebase.auth,
        restaurantLoginEmail(restaurantId),
        password
      );
    } catch (error) {
      if (this.firebaseErrorCode(error) === 'auth/user-disabled') {
        throw new Error('Restoran trenutno nije aktivan.');
      }
      throw new Error('Oznaka restorana ili lozinka nisu ispravni.');
    }
    const session = await this.createSession(credentials.user, true);
    if (session?.role !== 'admin') {
      await signOut(this.firebase.auth);
      throw new Error('Ovaj nalog nema administratorski pristup.');
    }
    this.sessionSubject.next(session);
    return session;
  }

  async loginPlatformOwner(email: string, password: string): Promise<AppSession> {
    await setPersistence(this.firebase.auth, browserLocalPersistence);
    let credentials: UserCredential;
    try {
      credentials = await signInWithEmailAndPassword(
        this.firebase.auth,
        email.trim().toLowerCase(),
        password
      );
    } catch {
      throw new Error('E-mail adresa ili lozinka nisu ispravni.');
    }
    if (!(await this.isPlatformOwner())) {
      await signOut(this.firebase.auth);
      throw new Error('Ovaj nalog nema pristup platformi.');
    }
    const session: AppSession = {
      uid: credentials.user.uid,
      role: 'platformOwner',
      restaurantId: '',
    };
    this.sessionSubject.next(session);
    return session;
  }

  async changeAdminPassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user?.email) {
      throw new Error('Administratorska sesija nije dostupna.');
    }
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    try {
      await reauthenticateWithCredential(user, credential);
    } catch {
      throw new Error('Trenutna lozinka nije ispravna.');
    }
    await updatePassword(user, newPassword);
  }

  async exchangeTableToken(token: string): Promise<AppSession> {
    await setPersistence(this.firebase.auth, browserLocalPersistence);
    let user = this.firebase.auth.currentUser;
    if (!user?.isAnonymous) {
      if (user) {
        await signOut(this.firebase.auth);
      }
      user = (await signInAnonymously(this.firebase.auth)).user;
    }

    const exchangeTableQr = httpsCallable<
      { token: string },
      ExchangeTableQrResponse
    >(this.firebase.functions, 'exchangeTableQr');
    await exchangeTableQr({ token });
    const session = await this.createSession(user, true);
    if (session?.role !== 'customer' || !session.tableId) {
      throw new Error('QR kod nije validan ili više nije aktivan.');
    }
    this.sessionSubject.next(session);
    return session;
  }

  async logout(): Promise<void> {
    await signOut(this.firebase.auth);
    this.sessionSubject.next(null);
  }

  private async createSession(
    user: User | null,
    forceRefresh = false
  ): Promise<AppSession | null> {
    if (!user) {
      return null;
    }
    if (!user.isAnonymous && await this.isPlatformOwner()) {
      return {
        uid: user.uid,
        role: 'platformOwner',
        restaurantId: '',
      };
    }
    const token = await user.getIdTokenResult(forceRefresh);
    const role = token.claims['role'];
    const restaurantId = token.claims['restaurantId'];
    const tableId = token.claims['tableId'];
    if (
      (role !== 'customer' && role !== 'admin') ||
      typeof restaurantId !== 'string'
    ) {
      return null;
    }
    return {
      uid: user.uid,
      role,
      restaurantId,
      tableId: typeof tableId === 'string' ? tableId : undefined,
    };
  }

  private async isPlatformOwner(): Promise<boolean> {
    try {
      const verifyOwner = httpsCallable<Record<string, never>, PlatformOwnerResponse>(
        this.firebase.functions,
        'verifyPlatformOwner'
      );
      const response = await verifyOwner({});
      return response.data.authorized === true;
    } catch {
      return false;
    }
  }

  private firebaseErrorCode(error: unknown): string | undefined {
    return typeof error === 'object' && error !== null && 'code' in error
      ? String(error.code)
      : undefined;
  }

}
